import { BridgeComponent, BridgeElement } from "@hotwired/hotwire-native-bridge";

/**
 * SingleActionComponent - Adds a sticky bottom bar with one action button
 *
 * ## Usage
 * Add the `bridge--single-action` controller to any element:
 *
 * ```erb
 * <div data-controller="bridge--single-action"
 *      data-bridge--single-action-title-value="Add to routine"
 *      data-bridge--single-action-icon-value="plus.circle.fill"
 *      data-bridge--single-action-color-value="#4f46e5"
 *      data-bridge--single-action-text-color-value="#ffffff"
 *      class="hidden">
 *   <a href="/add" data-bridge--single-action-target="action"></a>
 * </div>
 * ```
 *
 * ## Stimulus Values
 * - `title` (String): Button text label
 * - `icon` (String): SF Symbol name for the button
 * - `color` (String): Hex color for the button background
 * - `textColor` (String, optional): Hex color for the button text/icon
 * - `loading` (Boolean, optional): Shows native progress and disables taps.
 *   Set automatically while the form an `action` submit button belongs to is
 *   saving through it (see "Saving" below)
 * - `disabled` (Boolean, optional): Disables the native action
 * - `minimumWidth` (Number, optional): Uses intrinsic width with this minimum instead of full width
 *
 * ## Targets
 * - `action`: Element to click when the button is tapped
 *
 * ## Behavior
 * - Displays one full-width button at the bottom of the screen
 * - Uses glassEffect on iOS 26+
 * - When tapped, triggers the action target element's click handler
 *
 * ## Saving
 * When the `action` target is a form's submit button, the native button shows
 * its loading dots from the tap until the save is done: they go if it fails,
 * and stay while a successful save leaves the page (a redirect, or a modal
 * closing after it). A save that keeps the page clears them shortly after.
 */
export default class extends BridgeComponent {
  static component = "single-action";
  static targets = ["action"];
  static values = {
    title: String,
    icon: String,
    color: String,
    textColor: String,
    loading: Boolean,
    disabled: Boolean,
    minimumWidth: Number,
  };

  connect() {
    super.connect();
    this.bridgeConnected = true;
    this.#notifyBridgeOfConnect();

    this.submitForm = this.#submitForm;
    this.submitForm?.addEventListener("turbo:submit-start", this.#submitStarted);
    this.submitForm?.addEventListener("turbo:submit-end", this.#submitEnded);
    document.addEventListener("turbo:before-visit", this.#leaving);
  }

  disconnect() {
    clearTimeout(this.settleTimeout);
    this.submitForm?.removeEventListener("turbo:submit-start", this.#submitStarted);
    this.submitForm?.removeEventListener("turbo:submit-end", this.#submitEnded);
    document.removeEventListener("turbo:before-visit", this.#leaving);

    this.bridgeConnected = false;
    super.disconnect();
    this.send("disconnect", {}, () => { });
  }

  titleValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  iconValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  colorValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  textColorValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  loadingValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  disabledValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  minimumWidthValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  get #loading() {
    return (this.hasLoadingValue && this.loadingValue) || !!this.submitting;
  }

  get #submitForm() {
    if (!this.hasActionTarget || this.actionTarget.type !== "submit") return null;

    return this.actionTarget.form;
  }

  // Only this button's save: the form's other submit buttons (one that saves
  // on its way to another page, say) keep the native button as it is.
  #submitStarted = (event) => {
    if (event.detail.formSubmission?.submitter !== this.actionTarget) return;

    clearTimeout(this.settleTimeout);
    this.saving = true;
    this.leavingPage = false;
    this.#setSubmitting(true);
  };

  #submitEnded = (event) => {
    if (!this.saving) return;

    this.saving = false;
    if (!event.detail.success) {
      this.#setSubmitting(false);
    } else if (!this.leavingPage) {
      // A modal can close a moment after its save responds; give it time to.
      this.settleTimeout = setTimeout(() => this.#setSubmitting(false), 1000);
    }
  };

  // Kept off the `loading` value: Turbo caches the page's markup, and a
  // cached page shown again must not come back saving.
  #setSubmitting(submitting) {
    this.submitting = submitting;
    this.#scheduleBridgeUpdate();
  }

  #leaving = () => {
    this.leavingPage = true;
    clearTimeout(this.settleTimeout);
  };

  #scheduleBridgeUpdate() {
    if (!this.bridgeConnected || this.bridgeUpdateScheduled) return;

    this.bridgeUpdateScheduled = true;
    queueMicrotask(() => {
      this.bridgeUpdateScheduled = false;

      if (this.bridgeConnected) this.#notifyBridgeOfConnect();
    });
  }

  #notifyBridgeOfConnect() {
    const data = {
      title: this.hasTitleValue ? this.titleValue : "Action",
      icon: this.hasIconValue ? this.iconValue : "star.fill",
      color: this.hasColorValue ? this.colorValue : "#4f46e5",
      textColor: this.hasTextColorValue ? this.textColorValue : null,
      loading: this.#loading,
      disabled: this.hasDisabledValue && this.disabledValue,
      minimumWidth: this.hasMinimumWidthValue ? this.minimumWidthValue : null,
    };

    this.send("connect", data, () => {
      // Callback when native button is tapped
      if (!this.#loading && !this.disabledValue && this.hasActionTarget) {
        new BridgeElement(this.actionTarget).click();
      }
    });
  }
}
